import * as THREE from 'three';
import { PLANET_R } from '../config.js';
import { groundRadius } from './placement.js';

// A trail of flattened-grass boot prints that fades out behind the
// character. Uses a fixed pool of flat oval meshes so we never allocate
// during gameplay — when the pool wraps, the oldest print is reused.
//
// Prints are only emitted on the ground (not while airborne) once the
// character has moved a stride's worth of distance, alternating left/right
// each time.

const POOL_SIZE   = 60;
const STRIDE_DIST = 0.48;     // world distance between successive prints
const LIFETIME    = 4.5;      // seconds until fully faded
const SIDE_OFFSET = 0.13;     // perpendicular offset (world units)
const LIFT        = 0.02;     // tiny lift above the ground to avoid z-fighting
const MAX_OPACITY = 0.42;

const pool = [];
let nextIdx     = 0;
let stepSide    = 1;
let lastWorldPos = null;

const _right  = new THREE.Vector3();
const _pos    = new THREE.Vector3();
const _dir    = new THREE.Vector3();
const _target = new THREE.Vector3();

// Build a symmetric boot-outline shape — wider toe arc up front, narrower
// heel arc at the back, straight sides tapering between. Reads clearly as
// a boot from overhead at the game's scale.
function makeBootGeometry() {
  const shape = new THREE.Shape();
  const toeHW  = 0.06;
  const heelHW = 0.045;
  const toeY   = 0.08;
  const heelY  = -0.09;

  shape.moveTo(heelHW, heelY);
  shape.lineTo(toeHW, toeY);
  shape.absarc(0, toeY, toeHW, 0, Math.PI, false);       // toe semicircle
  shape.lineTo(-heelHW, heelY);
  shape.absarc(0, heelY, heelHW, Math.PI, Math.PI * 2, false); // heel

  const geo = new THREE.ShapeGeometry(shape, 12);
  geo.rotateX(-Math.PI / 2);           // flat on XZ plane, normal +Y
  return geo;
}

export function initFootprints(scene) {
  const geo = makeBootGeometry();

  for (let i = 0; i < POOL_SIZE; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: 0x324020,                 // a few shades darker than grass — trampled
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.visible = false;
    scene.add(mesh);
    pool.push({ mesh, born: -1 });
  }
}

export function updateFootprints(charPos, charForward, altitude, planet) {
  const t = performance.now() / 1000;

  // ---- Emit a new print if we've moved far enough on the ground ----
  const gR = groundRadius(planet, charPos);
  _pos.copy(charPos).multiplyScalar(gR);

  if (!lastWorldPos) lastWorldPos = _pos.clone();

  const stride = _pos.distanceTo(lastWorldPos);
  if (altitude < 0.08 && stride > STRIDE_DIST) {
    // Right-hand tangent at the character's current position.
    _right.crossVectors(charPos, charForward).normalize();

    // Offset in world space then renormalise to get a direction on the
    // sphere — good enough for this small displacement.
    _dir.copy(charPos).multiplyScalar(PLANET_R)
        .addScaledVector(_right, stepSide * SIDE_OFFSET)
        .normalize();

    const printR = groundRadius(planet, _dir);
    const entry = pool[nextIdx];
    nextIdx = (nextIdx + 1) % POOL_SIZE;

    entry.mesh.position.copy(_dir).multiplyScalar(printR + LIFT);
    entry.mesh.up.copy(_dir);
    _target.copy(entry.mesh.position).add(charForward);
    entry.mesh.lookAt(_target);
    entry.mesh.visible = true;
    entry.mesh.material.opacity = MAX_OPACITY;
    entry.born = t;

    stepSide = -stepSide;
    lastWorldPos.copy(_pos);
  } else if (stride > STRIDE_DIST * 3) {
    // Teleport / huge jump — resync without spawning a train of prints.
    lastWorldPos.copy(_pos);
  }

  // ---- Fade active prints ----
  for (const p of pool) {
    if (p.born < 0) continue;
    const age = t - p.born;
    if (age > LIFETIME) {
      p.mesh.visible = false;
      p.born = -1;
    } else {
      p.mesh.material.opacity = MAX_OPACITY * (1 - age / LIFETIME);
    }
  }
}
